/**
 * RichEmailEditor — Shared rich text editor for email templates
 *
 * Features:
 * - Visual WYSIWYG editing with contentEditable
 * - HTML source view toggle
 * - Formatting toolbar (bold, italic, lists, headings, etc.)
 * - Merge field {{token}} support
 * - Theme-compatible styling
 * - React 19 compatible
 *
 * Usage:
 * ```tsx
 * <RichEmailEditor
 *   value={htmlContent}
 *   onChange={setHtmlContent}
 *   placeholder="Type your email here..."
 *   mergeFields={['client_name', 'case_ref']}
 * />
 * ```
 */

import { useState, useRef, useEffect } from 'react';
import './RichEmailEditor.css';

interface RichEmailEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  height?: number;
  mergeFields?: string[];
  showSourceToggle?: boolean;
}

export default function RichEmailEditor({
  value,
  onChange,
  placeholder = 'Type your email message here...',
  height = 300,
  mergeFields = [],
  showSourceToggle = true,
}: RichEmailEditorProps) {
  const [viewMode, setViewMode] = useState<'visual' | 'source'>('visual');
  const editorRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Sync external changes to editor
  useEffect(() => {
    if (editorRef.current && viewMode === 'visual' && !isFocused) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value, viewMode, isFocused]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const insertMergeField = (field: string) => {
    const token = `{{${field}}}`;
    if (viewMode === 'visual') {
      execCommand('insertText', token);
    } else {
      onChange(value + ' ' + token);
    }
  };

  const btnStyle: React.CSSProperties = {
    padding: '4px 10px',
    border: '1px solid var(--border-primary)',
    borderRadius: 4,
    fontSize: '0.75rem',
    cursor: 'pointer',
    transition: 'all 0.15s',
    fontWeight: 500,
  };

  return (
    <div className="rich-email-editor">
      {/* Header with mode toggle and merge fields */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {showSourceToggle && (
            <>
              <button
                type="button"
                onClick={() => setViewMode('visual')}
                style={{
                  ...btnStyle,
                  background:
                    viewMode === 'visual' ? 'var(--primary-100)' : 'var(--surface-primary)',
                  color: viewMode === 'visual' ? 'var(--primary-700)' : 'var(--text-secondary)',
                  borderColor:
                    viewMode === 'visual' ? 'var(--primary-400)' : 'var(--border-primary)',
                }}
              >
                ✏️ Visual
              </button>
              <button
                type="button"
                onClick={() => setViewMode('source')}
                style={{
                  ...btnStyle,
                  background:
                    viewMode === 'source' ? 'var(--primary-100)' : 'var(--surface-primary)',
                  color: viewMode === 'source' ? 'var(--primary-700)' : 'var(--text-secondary)',
                  borderColor:
                    viewMode === 'source' ? 'var(--primary-400)' : 'var(--border-primary)',
                }}
              >
                {'</>'} HTML
              </button>
            </>
          )}
        </div>

        {/* Merge field helper */}
        {mergeFields.length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>Merge fields:</span>
            {mergeFields.slice(0, 5).map((field) => (
              <button
                key={field}
                type="button"
                onClick={() => insertMergeField(field)}
                style={{
                  padding: '2px 6px',
                  border: '1px solid var(--border-primary)',
                  borderRadius: 4,
                  fontSize: '0.68rem',
                  cursor: 'pointer',
                  background: 'var(--surface-tertiary)',
                  color: 'var(--text-secondary)',
                  fontFamily: 'monospace',
                }}
                title={`Insert {{${field}}}`}
              >
                {`{{${field}}}`}
              </button>
            ))}
            {mergeFields.length > 5 && (
              <span style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)' }}>
                +{mergeFields.length - 5} more
              </span>
            )}
          </div>
        )}
      </div>

      {viewMode === 'visual' && (
        <div
          className="editor-toolbar"
          style={{
            display: 'flex',
            gap: 4,
            padding: 8,
            borderBottom: '1px solid var(--border-primary)',
            background: 'var(--surface-secondary)',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={() => execCommand('bold')}
            title="Bold"
            className="toolbar-btn"
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            onClick={() => execCommand('italic')}
            title="Italic"
            className="toolbar-btn"
          >
            <em>I</em>
          </button>
          <button
            type="button"
            onClick={() => execCommand('underline')}
            title="Underline"
            className="toolbar-btn"
          >
            <u>U</u>
          </button>
          <div style={{ width: 1, background: 'var(--border-primary)', margin: '0 4px' }} />
          <button
            type="button"
            onClick={() => execCommand('insertUnorderedList')}
            title="Bullet List"
            className="toolbar-btn"
          >
            • List
          </button>
          <button
            type="button"
            onClick={() => execCommand('insertOrderedList')}
            title="Numbered List"
            className="toolbar-btn"
          >
            1. List
          </button>
          <div style={{ width: 1, background: 'var(--border-primary)', margin: '0 4px' }} />
          <button
            type="button"
            onClick={() => execCommand('formatBlock', '<h2>')}
            title="Heading"
            className="toolbar-btn"
          >
            H2
          </button>
          <button
            type="button"
            onClick={() => execCommand('formatBlock', '<p>')}
            title="Paragraph"
            className="toolbar-btn"
          >
            P
          </button>
        </div>
      )}

      {/* Editor */}
      {viewMode === 'visual' ? (
        <div
          ref={editorRef}
          contentEditable
          className="editor-content"
          onInput={handleInput}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          style={{
            minHeight: height,
            padding: 12,
            border: '1px solid var(--border-primary)',
            borderTop: 'none',
            borderRadius: '0 0 6px 6px',
            fontSize: '0.9rem',
            color: 'var(--text-primary)',
            background: 'var(--surface-primary)',
            overflowY: 'auto',
            lineHeight: 1.6,
          }}
          data-placeholder={placeholder}
          suppressContentEditableWarning
        />
      ) : (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            width: '100%',
            height,
            padding: 12,
            border: '1px solid var(--border-primary)',
            borderRadius: 6,
            fontSize: '0.82rem',
            fontFamily: 'monospace',
            color: 'var(--text-primary)',
            background: 'var(--surface-primary)',
            resize: 'vertical',
            boxSizing: 'border-box',
            lineHeight: 1.6,
          }}
        />
      )}
    </div>
  );
}
